/**
 * World's Toughest Row - Main Entry Point
 * PERFORMANCE OPTIMIZED: Throttled updates, RAF-based rendering
 */

import './styles/main.css';
import { RACE_TITLE, RACE_ROUTE } from './config.js';
import { raceData } from './data/raceData.js';
import { MapController } from './map/mapbox.js';
import { TrackRenderer } from './visualization/tracks.js';
import { BoatRenderer } from './visualization/boats.js';
import { BarChartView } from './visualization/barChart.js';
import { TimelineController } from './ui/timeline.js';
import { LeaderboardController } from './ui/leaderboard.js';
import { TeamPanelController } from './ui/teamPanel.js';
import { MobileDrawerController } from './ui/mobileDrawer.js';

/**
 * Throttle function for performance
 */
function throttle(fn, wait) {
  let lastTime = 0;
  let rafId = null;

  return function(...args) {
    const now = performance.now();

    if (now - lastTime >= wait) {
      lastTime = now;
      fn.apply(this, args);
    } else if (!rafId) {
      rafId = requestAnimationFrame(() => {
        rafId = null;
        lastTime = performance.now();
        fn.apply(this, args);
      });
    }
  };
}

class RaceVisualization {
  constructor() {
    // Components
    this.map = null;
    this.tracks = null;
    this.boats = null;
    this.barChart = null;
    this.timeline = null;
    this.leaderboard = null;
    this.teamPanel = null;
    this.mobileDrawer = null;

    // State
    this.selectedTeam = null;
    this.isLive = true;
    this.currentDisplayTime = null;
    this.cachedTeamsAtTime = null;
    this.isBarChartView = false;
    this.mapUpdatePending = false;

    // Map update uses rAF coalescing (faster than throttle for rapid events)
    this.throttledMapUpdate = () => {
      if (!this.mapUpdatePending) {
        this.mapUpdatePending = true;
        requestAnimationFrame(() => {
          this.updateVisualizationFast();
          this.mapUpdatePending = false;
        });
      }
    };
    this.throttledTimelineUpdate = throttle((time) => this.updateForTime(time), 16);

    this.init();
  }

  /**
   * Populate header + document title from the race config
   */
  applyRaceLabels() {
    if (RACE_TITLE) document.title = RACE_TITLE;

    const titleEl = document.getElementById('race-title');
    if (titleEl && RACE_TITLE) titleEl.textContent = RACE_TITLE;

    const routeEl = document.getElementById('race-route');
    if (routeEl) routeEl.textContent = RACE_ROUTE || '';
  }

  async init() {
    try {
      // Apply race-specific labels from config
      this.applyRaceLabels();

      // Check for Mapbox token
      const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN;
      if (!mapboxToken) {
        throw new Error('Mapbox token not found. Please set VITE_MAPBOX_TOKEN environment variable.');
      }

      // Initialize map
      this.map = new MapController('map', mapboxToken);
      await this.map.whenReady();

      // Fetch race data
      await raceData.fetch();

      // Initialize visualization layers
      const svgOverlay = document.getElementById('d3-overlay');
      this.tracks = new TrackRenderer(svgOverlay, this.map);
      this.boats = new BoatRenderer(svgOverlay, this.map);
      this.barChart = new BarChartView(this.map);
      this.barChart.setCloseHandler(() => this.toggleBarChartView());

      // Initialize UI components
      this.timeline = new TimelineController(raceData);
      this.leaderboard = new LeaderboardController();
      this.teamPanel = new TeamPanelController();
      this.mobileDrawer = new MobileDrawerController();

      // Setup interactions
      this.setupInteractions();

      // Setup mobile drawer handlers
      this.setupMobileDrawer();

      // Initial render
      this.renderVisualization(raceData.teams);
      this.updateStats(raceData.teams);

      // Start live updates
      raceData.startPolling();

      // Fit map to show all teams
      setTimeout(() => {
        this.map.fitToTeams(raceData.teams);
      }, 500);

      // Setup live clock
      this.startClock();

      console.log('🚣 Race visualization initialized');
    } catch (error) {
      console.error('Failed to initialize:', error);
    }
  }

  setupInteractions() {
    // Subscribe to data updates (less frequent, full update)
    raceData.subscribe((teams, timeRange) => {
      if (this.isLive) {
        this.renderVisualization(teams);
        this.updateStats(teams);
      }
    });

    // Map move/zoom - FAST path using throttled canvas redraw
    this.map.onMove(() => {
      this.throttledMapUpdate();
    });

    // Timeline time changes - throttled for smooth scrubbing
    this.timeline.setTimeChangeHandler((time) => {
      this.currentDisplayTime = time;
      this.throttledTimelineUpdate(time);
    });

    // Timeline live mode changes
    this.timeline.setLiveChangeHandler((isLive) => {
      this.isLive = isLive;
      this.cachedTeamsAtTime = null; // Clear cache

      // Sync mobile drawer live indicator
      this.mobileDrawer.updateLiveIndicator(isLive);

      if (isLive) {
        this.renderVisualization(raceData.teams);
        this.updateStats(raceData.teams);
      }
    });

    // Leaderboard team selection
    this.leaderboard.setSelectHandler((team, flyTo) => {
      this.selectedTeam = team?.serial || null;
      this.mobileDrawer.setSelectedTeam(this.selectedTeam);
      this.updateSelection();

      if (flyTo && team) {
        const pos = team.interpolated || team.latest;
        if (pos) {
          this.map.flyTo(pos.lng, pos.lat, 6);
        }
      }
    });

    // Leaderboard comparison
    this.leaderboard.setCompareHandler((teams) => {
      this.teamPanel.update(teams);
      if (teams.length > 0) {
        this.teamPanel.show();
      }
    });

    // Boat click handler
    this.boats.setClickHandler((team) => {
      this.leaderboard.selectTeam(team.serial);
    });

    // Fullscreen button
    document.getElementById('fullscreen-btn').addEventListener('click', () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        document.documentElement.requestFullscreen();
      }
    });

    // Bar chart toggle
    const barChartBtn = document.getElementById('toggle-barchart');
    barChartBtn.addEventListener('click', () => this.toggleBarChartView());

    // Cursor coordinates (throttled)
    const updateCoords = throttle((e) => {
      const { lng, lat } = e.lngLat;
      const latDir = lat >= 0 ? 'N' : 'S';
      const lngDir = lng >= 0 ? 'E' : 'W';
      document.getElementById('cursor-coords').textContent =
        `${Math.abs(lat).toFixed(4)}°${latDir}, ${Math.abs(lng).toFixed(4)}°${lngDir}`;
    }, 50);

    this.map.map.on('mousemove', updateCoords);
  }

  setupMobileDrawer() {
    // Team selection from drawer
    this.mobileDrawer.setTeamSelectHandler((team) => {
      this.selectedTeam = team?.serial || null;
      this.leaderboard.selectTeam(team?.serial);
      this.updateSelection();

      // Fly to team location
      if (team) {
        const pos = team.interpolated || team.latest;
        if (pos) {
          this.map.flyTo(pos.lng, pos.lat, 6);
        }
      }
    });

    // Progress button - toggle bar chart view
    this.mobileDrawer.setProgressHandler(() => this.toggleBarChartView());

    // Fullscreen button
    this.mobileDrawer.setFullscreenHandler(() => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        document.documentElement.requestFullscreen();
      }
    });
  }

  /**
   * Full visualization render (data change)
   */
  renderVisualization(teams) {
    this.tracks.render(teams, this.selectedTeam);
    this.boats.render(teams, this.selectedTeam);
    this.leaderboard.update(teams);

    // Sync mobile drawer leaderboard
    this.mobileDrawer.updateLeaderboard(teams);
    this.mobileDrawer.setSelectedTeam(this.selectedTeam);
  }

  /**
   * Fast visualization update (map pan/zoom) - just redraw canvases
   */
  updateVisualizationFast() {
    // Just redraw - no data recalculation
    // Use immediate=true since we're already throttled
    this.tracks.drawCanvas(true);
    this.boats.draw(true);
  }

  /**
   * Update for timeline scrubbing
   */
  updateForTime(time) {
    const teamsAtTime = raceData.getPositionsAtTime(time);
    this.cachedTeamsAtTime = teamsAtTime;

    // Fast path - only update tracks and boats, not leaderboard
    this.tracks.render(teamsAtTime, this.selectedTeam);
    this.boats.render(teamsAtTime, this.selectedTeam);

    // Debounced leaderboard update (less critical)
    this.debouncedLeaderboardUpdate(teamsAtTime);
  }

  // Debounced leaderboard (doesn't need 60fps)
  _leaderboardTimeout = null;
  debouncedLeaderboardUpdate(teams) {
    if (this._leaderboardTimeout) {
      clearTimeout(this._leaderboardTimeout);
    }
    this._leaderboardTimeout = setTimeout(() => {
      this.leaderboard.update(teams);
      this.updateStats(teams);
    }, 100);
  }

  /**
   * Update selection state
   */
  updateSelection() {
    const teams = this.isLive
      ? raceData.teams
      : (this.cachedTeamsAtTime || raceData.getPositionsAtTime(this.currentDisplayTime));

    this.tracks.render(teams, this.selectedTeam);
    this.boats.render(teams, this.selectedTeam);
  }

  updateStats(teams) {
    const leader = teams[0];
    if (leader) {
      const pos = leader.interpolated || leader.latest;
      document.getElementById('leader-dtf').textContent =
        pos?.dtf ? pos.dtf.toFixed(0) : '--';
    }

    // Course length ≈ the furthest distance-to-finish ever observed
    // (a boat near the start), so it adapts to any race.
    let courseDistance = 0;
    teams.forEach(t => {
      (t.positions || []).forEach(p => {
        if (p.dtf > courseDistance) courseDistance = p.dtf;
      });
      const latestDtf = t.latest?.dtf ?? 0;
      if (latestDtf > courseDistance) courseDistance = latestDtf;
    });
    if (courseDistance > 0) {
      document.getElementById('race-distance').textContent =
        Math.round(courseDistance).toLocaleString();
    }

    const activeTeams = teams.filter(t => {
      const pos = t.interpolated || t.latest;
      return pos && pos.speed > 0;
    }).length;
    document.getElementById('active-teams').textContent = activeTeams;
  }

  /**
   * Toggle between map view and bar chart view
   */
  async toggleBarChartView() {
    const btn = document.getElementById('toggle-barchart');

    if (this.isBarChartView) {
      // Switch back to map view
      btn.classList.remove('active');
      this.mobileDrawer.setProgressActive(false);
      await this.barChart.hide();
      this.isBarChartView = false;

      // Hide UI elements that were hidden
      document.getElementById('leaderboard').style.opacity = '1';
      document.getElementById('timeline').style.opacity = '1';
      document.getElementById('ocean-stats').style.opacity = '1';
    } else {
      // Switch to bar chart view
      btn.classList.add('active');
      this.mobileDrawer.setProgressActive(true);

      // Update bar chart with current teams
      const teams = this.isLive
        ? raceData.teams
        : (this.cachedTeamsAtTime || raceData.getPositionsAtTime(this.currentDisplayTime));

      this.barChart.setTeams(teams);

      // Fade out UI elements
      document.getElementById('leaderboard').style.opacity = '0';
      document.getElementById('timeline').style.opacity = '0';
      document.getElementById('ocean-stats').style.opacity = '0';

      await this.barChart.show();
      this.isBarChartView = true;
    }
  }

  startClock() {
    const updateClock = () => {
      const now = new Date();
      const options = {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZoneName: 'short',
      };
      const timeString = new Intl.DateTimeFormat('en-US', options).format(now);
      document.getElementById('current-time').textContent = timeString;

      // Sync mobile drawer clock
      this.mobileDrawer.updateClock(timeString);
    };

    updateClock();
    setInterval(updateClock, 1000);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new RaceVisualization();
});
